import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ValidationError } from 'yup';

import { Modal } from '@/common';
import { FileDropZone } from '@/components/common';
import { Label, LABEL_TYPE, Spinner } from '@/components/generic';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { UPLOAD_PICKER_TYPES } from '@/constants';
import { AssetScope, AssetSource, AssetType } from '@/enums';
import { showToast, ToastType } from '@/helpers/toast';
import { useFileUpload } from '@/hooks';
import { useUploadFilePicker } from '@/hooks/useFilePicker';
import { AssetDetails } from '@/pages/Event/Asset/AssetDetails';
import { AssetItem } from '@/pages/Event/Asset/AssetItem';
import { createFolderSchema } from '@/pages/Event/UploadContent/RequestsValidation';
import { FolderMusicModal } from '@/pages/Event/UploadContent/components/FolderMusicModal';
import { SelectionBar } from '@/pages/Event/UploadContent/components/SelectionBar';
import { CheckIcon, ChevronUpNewIcon, DeleteIcon, MusicIcon, VideoPlayNewIcon } from '@/resources';
import {
  useDeleteAssetMutation,
  useDeleteAssetsMutation,
  useGetEventAssets,
  useUpdateTagMutation
} from '@/services';
import { Asset, Tag, ThemeProp } from '@/types';

const FOLDER_ASSETS_PAGE_SIZE = 50;

type FolderItemProps = {
  eventId: string;
  organizationId?: number | null;
  folder: Tag;
  isExpandedByDefault?: boolean;
  hint?: string;
  organizationPermissions: string[];
  isSelectionActive?: boolean;
  onSelectionStart?: (folderId: number) => void;
};

export const FolderItem = ({
  eventId,
  organizationId,
  folder,
  isExpandedByDefault = false,
  hint,
  organizationPermissions,
  isSelectionActive = false,
  onSelectionStart
}: FolderItemProps) => {
  const [t] = useTranslation('common');
  const [isExpanded, setIsExpanded] = useState(isExpandedByDefault);
  const [isRenaming, setIsRenaming] = useState(false);
  const [folderName, setFolderName] = useState(folder.name);
  const [selectedAssetIds, setSelectedAssetIds] = useState<number[]>([]);
  const isSelectMode = selectedAssetIds.length > 0;
  const [isDeleteSelectedOpen, setIsDeleteSelectedOpen] = useState(false);

  useEffect(() => {
    if (!isSelectionActive) {
      setSelectedAssetIds([]);
    }
  }, [isSelectionActive]);

  const toggleAssetSelection = (assetId: number) => {
    if (selectedAssetIds.length === 0) {
      onSelectionStart?.(folder.id);
    }
    setSelectedAssetIds((ids) =>
      ids.includes(assetId) ? ids.filter((id) => id !== assetId) : [...ids, assetId]
    );
  };

  const { mutateAsync: deleteAssets, isLoading: isDeletingSelected } =
    useDeleteAssetsMutation(queryClient);

  const handleDeleteSelected = async () => {
    setIsDeleteSelectedOpen(false);
    const failedCount = await deleteAssets(selectedAssetIds);
    setSelectedAssetIds([]);
    if (failedCount > 0) {
      showToast(ToastType.ERROR, t('selectedFilesDeleteError'));
    } else {
      showToast(ToastType.SUCCESS, t('selectedFilesDeleted'));
    }
  };
  const isRenameCancelledRef = useRef(false);
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Focus the input once it appears; onFocus then selects the name, ready to overwrite.
  useEffect(() => {
    if (isRenaming) {
      renameInputRef.current?.focus();
    }
  }, [isRenaming]);

  useEffect(() => {
    if (isExpandedByDefault) {
      setIsExpanded(true);
    }
  }, [isExpandedByDefault]);

  useEffect(() => {
    setFolderName(folder.name);
  }, [folder.name]);

  const { mutate: updateTag } = useUpdateTagMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('folderUpdated'));
    },
    onError: (error) => {
      setFolderName(folder.name);
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
    }
  });

  const startRenaming = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFolderName(folder.name);
    isRenameCancelledRef.current = false;
    setIsRenaming(true);
  };

  const cancelRenaming = () => {
    isRenameCancelledRef.current = true;
    setFolderName(folder.name);
    setIsRenaming(false);
  };

  const saveFolderName = async () => {
    if (isRenameCancelledRef.current) {
      return;
    }
    setIsRenaming(false);
    const name = folderName.trim();
    if (name === folder.name) {
      return;
    }
    try {
      await createFolderSchema.validate({ name });
      setFolderName(name);
      updateTag({ id: folder.id, name });
    } catch (error) {
      setFolderName(folder.name);
      showToast(ToastType.ERROR, (error as ValidationError).message);
    }
  };
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [showAsset, setShowAsset] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isMusicModalOpen, setIsMusicModalOpen] = useState(false);

  const { uploadFiles, isUploading } = useFileUpload({
    pickerTypes: UPLOAD_PICKER_TYPES.MEDIA,
    eventId,
    organizationId,
    assetScope: AssetScope.PRIVATE,
    assetTag: folder.name
  });

  const { openFilePicker, hiddenInputProps } = useUploadFilePicker({
    pickerTypes: UPLOAD_PICKER_TYPES.MEDIA,
    multiple: true,
    disabled: isUploading,
    onFilesSelected: uploadFiles
  });

  const { data: folderAssetsData, refetch: refetchFolderAssets } = useGetEventAssets(
    eventId,
    [`${folder.id}`],
    FOLDER_ASSETS_PAGE_SIZE,
    undefined,
    undefined,
    undefined,
    true,
    isExpanded
  );

  const { data: folderAudioAssetsData, refetch: refetchFolderAudioAssets } = useGetEventAssets(
    eventId,
    [`${folder.id}`],
    FOLDER_ASSETS_PAGE_SIZE,
    undefined,
    [AssetType.AUDIO.toString()],
    undefined,
    true,
    true
  );

  const folderAudioAssets: Asset[] =
    folderAudioAssetsData?.pages.flatMap((page) => page.data?.items) ?? [];

  const folderSong =
    folderAudioAssets.find((asset) => asset.assetSource === AssetSource.MUSIC_PROVIDER) ??
    folderAudioAssets[0];

  const folderAssets: Asset[] = (
    folderAssetsData?.pages.flatMap((page) => page.data?.items) ?? []
  ).filter((asset) => asset.id !== folderSong?.id);

  const { mutate: deleteAsset } = useDeleteAssetMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('assetSuccessfullyDeleted'));
      setIsDeleteOpen(false);
    },
    onError: (error) => {
      if (error.response.data.ErrorMessages[0].Code === 'ArgumentNull') {
        showToast(ToastType.ERROR, t('userDeleteAssetError'));
      } else {
        showToast(ToastType.ERROR, t('assetDeleteError'));
      }
    }
  });

  return (
    <div className='mb-4 w-full cursor-pointer rounded-2xl border-[1.5px] border-dashed border-blue-powder bg-primary-white p-4'>
      <div
        className='flex flex-row items-center justify-between'
        onClick={() => setIsExpanded(!isExpanded)}>
        <div className='flex flex-row items-center gap-2'>
          {isRenaming ? (
            <input
              ref={renameInputRef}
              maxLength={35}
              value={folderName}
              className='rounded-md border border-secondary-blue-gray px-2 py-1 text-[20px] font-medium text-primary-black outline-none focus:border-primary-blue'
              onFocus={(e) => e.target.select()}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setFolderName(e.target.value)}
              onBlur={saveFolderName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur();
                } else if (e.key === 'Escape') {
                  cancelRenaming();
                }
              }}
            />
          ) : (
            <button
              type='button'
              className='cursor-text text-left text-[20px] font-medium'
              onClick={startRenaming}>
              {folderName}
            </button>
          )}
          {hint && !isRenaming && (
            <Label
              text={hint}
              type={LABEL_TYPE.PARAGRAPH1}
              className='!text-[20px] !text-secondary-gray'
            />
          )}
        </div>
        <div className='flex flex-row items-center gap-3'>
          {folderSong ? (
            <div className='flex h-10 flex-row items-center gap-2 rounded-[10px] bg-primary-gray px-4'>
              <MusicIcon className='size-4 shrink-0 fill-primary-blue' />
              <Label
                text={folderSong.assetName}
                className='max-w-[160px] truncate !text-sm !font-bold !text-primary-blue'
              />
              <DeleteIcon
                className='size-3 shrink-0 cursor-pointer text-secondary-gray hover:text-accent-red'
                onClick={(e) => {
                  e.stopPropagation();
                  deleteAsset(folderSong.id);
                }}
              />
            </div>
          ) : (
            <div
              className='flex h-10 cursor-pointer flex-row items-center gap-2 rounded-[10px] bg-primary-gray px-4'
              onClick={(e) => {
                e.stopPropagation();
                setIsMusicModalOpen(true);
              }}>
              <Label text={t('addMusic')} className='!text-base !font-bold !text-primary-blue' />
            </div>
          )}
          <div className='flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-[10px] bg-primary-gray'>
            <ChevronUpNewIcon
              className={`size-6 text-primary-blue transition-transform ${
                isExpanded ? '' : 'rotate-180'
              }`}
            />
          </div>
        </div>
      </div>
      {isExpanded && (
        <div className='mt-4'>
          <div className='mb-4 flex w-full border-t border-blue-powder'></div>
          <input {...hiddenInputProps} />
          <div className='grid grid-cols-[repeat(auto-fill,172px)] gap-2.5'>
            {folderAssets.map((asset) => {
              const isSelected = selectedAssetIds.includes(asset.id);
              return (
                <div key={asset.id} className='group relative'>
                  <AssetItem
                    asset={asset}
                    selectedVideoUrl=''
                    setSelectedAsset={
                      setSelectedAsset as Dispatch<SetStateAction<Asset | ThemeProp | null>>
                    }
                    setShowAsset={setShowAsset}
                    setIsDeleteOpen={setIsDeleteOpen}
                  />
                  {isSelectMode && (
                    <button
                      type='button'
                      aria-label={t('selectFile')}
                      aria-pressed={isSelected}
                      className='absolute inset-0 z-10 cursor-pointer'
                      onClick={() => toggleAssetSelection(asset.id)}
                    />
                  )}

                  <button
                    type='button'
                    aria-label={t('selectFile')}
                    aria-pressed={isSelected}
                    className={`absolute left-2.5 top-3 z-20 flex size-7 items-center justify-center ${
                      isSelectMode
                        ? 'opacity-100'
                        : 'opacity-0 group-hover:opacity-100 focus:opacity-100'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleAssetSelection(asset.id);
                    }}>
                    <span
                      className={`flex size-4 items-center justify-center rounded-sm border ${
                        isSelected
                          ? 'border-primary-blue bg-primary-blue'
                          : 'border-secondary-steel-blue bg-primary-white'
                      }`}>
                      {isSelected && <CheckIcon className='size-2.5 fill-primary-white' />}
                    </span>
                  </button>
                </div>
              );
            })}
            <FileDropZone
              onDrop={uploadFiles}
              allowMultipleFiles
              disabled={isUploading}
              onClick={openFilePicker}
              className={`flex aspect-square w-full flex-col items-center justify-center rounded-2xl border-[1.5px] border-dashed border-blue-powder ${isUploading ? '' : 'cursor-pointer'}`}
              activeClassName='bg-color-light-blue ring-4 ring-accent-light-blue'>
              {isUploading ? (
                <>
                  <div className='size-6 overflow-hidden'>
                    <Spinner />
                  </div>
                  <Label
                    text={t('uploading')}
                    type={LABEL_TYPE.PARAGRAPH2}
                    className='!text-center !text-secondary-gray'
                  />
                </>
              ) : (
                <>
                  <VideoPlayNewIcon className='mb-3 size-10' />
                  <span className='px-2 text-center text-sm text-secondary-gray'>
                    {t('dragAndDropFilesHere')}
                  </span>
                  <span className='mt-1 text-sm font-semibold text-primary-blue'>
                    {t('uploadFiles')}
                  </span>
                </>
              )}
            </FileDropZone>
          </div>
        </div>
      )}
      <Modal
        modalClasses='max-h-[75%] max-w-[600px] min-w-[300px] lg:min-w-[600px]'
        height='h-full'
        isOpen={showAsset}
        label=''
        onClose={() => setShowAsset(false)}>
        {showAsset && <AssetDetails asset={selectedAsset} />}
      </Modal>
      {selectedAssetIds.length > 0 && (
        <SelectionBar
          selectedCount={selectedAssetIds.length}
          isAllSelected={folderAssets.every((asset) => selectedAssetIds.includes(asset.id))}
          isDeleting={isDeletingSelected}
          onSelectAll={() => setSelectedAssetIds(folderAssets.map((asset) => asset.id))}
          onDelete={() => setIsDeleteSelectedOpen(true)}
          onClose={() => setSelectedAssetIds([])}
        />
      )}
      {isDeleteSelectedOpen && (
        <div className='fixed left-0 top-0 z-50 size-full'>
          <ConfirmationModal
            isOpen={true}
            message={t('deleteSelectedFilesMessage', { count: selectedAssetIds.length })}
            showFullMessage
            onClose={() => setIsDeleteSelectedOpen(false)}
            onConfirm={handleDeleteSelected}
          />
        </div>
      )}
      {isDeleteOpen && selectedAsset && (
        <div className='fixed left-0 top-0 z-50 size-full'>
          <ConfirmationModal
            isOpen={true}
            message={t('deleteMessage', { item: selectedAsset.assetName })}
            onClose={() => setIsDeleteOpen(false)}
            onConfirm={() => deleteAsset(selectedAsset.id)}
          />
        </div>
      )}
      {isMusicModalOpen && (
        <FolderMusicModal
          isOpen={isMusicModalOpen}
          onClose={() => setIsMusicModalOpen(false)}
          eventId={eventId}
          organizationId={organizationId}
          tag={folder.name}
          organizationPermissions={organizationPermissions}
          onSave={() => {
            refetchFolderAssets();
            refetchFolderAudioAssets();
          }}
        />
      )}
    </div>
  );
};
